/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  Table,
  Badge,
  Group,
  Text,
  Select,
  TextInput,
  Pagination,
  Paper,
  ScrollArea,
  Loader,
  Center,
  Stack,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconSearch, IconPencil, IconTrash } from "@tabler/icons-react";

import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetPrograms } from "api/program/getprogram";
import { useDeleteProgram } from "api/program/deleteProgram";
import { useGetUserPermissions } from "api/permission/getpermission";
import { useUser } from "hooks/useUser";
import ProgramUpdateModal from "./edit";
import { textOverlap } from "utils/commonFunction";

type LocalProgram = {
  _id: string;
  name: string;
  description?: string;
  status?: string;
  parentDepartmentId?: string;
  createdAt?: string;
  updatedAt?: string;
};

export default function ProgramTable() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [opened, setOpened] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    null,
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [parentDept, setParentDept] = useState<string | null>(null);
  const [alpha, setAlpha] = useState<string | null>(null);

  // Get user and permissions
  const { user } = useUser();
  const userId = user?._id ?? "";
  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};

  const alphabet = useMemo(() => {
    const letters: string[] = [];
    for (let i = 65; i <= 90; i++) letters.push(String.fromCharCode(i));
    return letters;
  }, []);

  const { data: programsResp, isLoading: loadingPrograms } = useGetPrograms();
  const programs: LocalProgram[] = programsResp?.data ?? [];

  const { data: deptsResp } = useGetDepartments();
  const departmentsRaw = deptsResp?.data ?? [];
  const departments: { _id: string; name: string }[] = Array.isArray(
    departmentsRaw,
  )
    ? departmentsRaw
    : [];

  const deleteMutation = useDeleteProgram();

  // Helper function to check permissions
  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const hasPermission = (permissionKey: string) => {
    const value = getNested(permissions, permissionKey);
    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  // Check if user can edit programs
  const canEditProgram = hasPermission("program_management.update");

  // Check if user can delete programs
  const canDeleteProgram = hasPermission("program_management.delete");

  const parentOptions = useMemo(() => {
    if (departments.length > 0) {
      return departments.map((d) => ({ value: d._id, label: d.name }));
    }
    const unique = Array.from(
      new Set(programs.map((p) => p.parentDepartmentId || "").filter(Boolean)),
    );
    return unique.map((id) => ({ value: id, label: id }));
  }, [departments, programs]);

  const statusOptions = [
    { value: "", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const filtered = useMemo(() => {
    return programs.filter((d) => {
      if (statusFilter && statusFilter !== "") {
        if ((d.status || "").toLowerCase() !== statusFilter.toLowerCase())
          return false;
      }

      if (parentDept && parentDept !== "") {
        if (
          (d.parentDepartmentId || "").toLowerCase() !==
          parentDept.toLowerCase()
        )
          return false;
      }

      if (alpha && alpha !== null) {
        if (!d.name || !d.name.toUpperCase().startsWith(alpha)) return false;
      }

      if (search) {
        const hay = `${d.name} ${d.description || ""}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }

      return true;
    });
  }, [programs, search, statusFilter, parentDept, alpha]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const display = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const getParentName = (id?: string) => {
    if (!id) return "-";
    const found = departments.find((d) => d._id === id);
    return found ? found.name : id;
  };

  const handleEditClick = (id: string) => {
    setSelectedProgramId(id);
    setOpened(true);
  };

  const handleDeleteClick = (id: string) => {
    const ok = window.confirm("Are you sure you want to delete this program?");
    if (!ok) return;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {},
      },
    );
  };

  return (
    <div>
      <Paper shadow="sm" radius="md" p="lg" withBorder>
        <Stack spacing="md">
          <Group position="apart" align="center">
            <Text weight={600} size="lg">
              Programs
            </Text>

            <Group spacing="sm" align="center">
              <Select
                label="Rows"
                data={[
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                value={String(perPage)}
                onChange={(v) => {
                  setPerPage(Number(v) || 10);
                  setPage(1);
                }}
                sx={{ width: 120 }}
              />

              <Select
                label="Status"
                placeholder="All"
                data={statusOptions}
                value={statusFilter ?? ""}
                onChange={(v) => {
                  setStatusFilter(v || null);
                  setPage(1);
                }}
                sx={{ width: 160 }}
              />

              <Select
                label="Parent Department"
                placeholder="All"
                data={[{ value: "", label: "All" }, ...parentOptions]}
                value={parentDept ?? ""}
                onChange={(v) => {
                  setParentDept(v || null);
                  setPage(1);
                }}
                sx={{ width: 200 }}
              />

              <TextInput
                label="Search"
                placeholder="Search Name or Description"
                icon={<IconSearch size={16} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                sx={{ width: 300 }}
              />
            </Group>
          </Group>

          <ScrollArea>
            {loadingPrograms ? (
              <Center style={{ height: 200 }}>
                <Loader />
              </Center>
            ) : (
              <Table verticalSpacing="sm" highlightOnHover>
                <thead style={{ backgroundColor: "#f5f5f5" }}>
                  <tr>
                    <th>Name</th>
                    <th>Your Role</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Parent Department</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {display.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <Center style={{ height: 120 }}>
                          <Text color="dimmed">No records found</Text>
                        </Center>
                      </td>
                    </tr>
                  )}

                  {display.map((row: any) => (
                    <tr key={row._id}>
                      <td>
                        <Text size="sm" weight={500}>
                          {row.name}
                        </Text>
                      </td>

                      <td>
                        <Badge
                          color={row.userRole === "supervisor" ? "blue" : "gray"}
                          variant="light"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {row.userRole || "member"}
                        </Badge>
                      </td>

                      <td style={{ maxWidth: "3rem" }} className="max-w-[30%]">
                        <Text size="sm" lineClamp={2}>
                          {row.description || "-"}
                        </Text>
                      </td>

                      <td>
                        {String(row.status).toLowerCase() === "active" ? (
                          <Badge color="green" variant="light">
                            Active
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="light">
                            {row.status ? row.status : "Inactive"}
                          </Badge>
                        )}
                      </td>

                      <td>
                        <Text size="sm">{row.parentDepartmentName}</Text>
                      </td>

                      <td>
                        <Group spacing={8}>
                          <Tooltip
                            label={canEditProgram ? "Edit" : "You don't have permission to edit"}
                            withArrow
                          >
                            <ActionIcon
                              variant="default"
                              size="sm"
                              onClick={() => handleEditClick(row._id)}
                              disabled={!canEditProgram}
                              title={canEditProgram ? "Edit" : "No permission"}
                            >
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip
                            label={canDeleteProgram ? "Delete" : "You don't have permission to delete"}
                            withArrow
                          >
                            <ActionIcon
                              variant="default"
                              size="sm"
                              color="red"
                              onClick={() => handleDeleteClick(row._id)}
                              disabled={!canDeleteProgram || deleteMutation.isLoading}
                              title={canDeleteProgram ? "Delete" : "No permission"}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </ScrollArea>

          <Group position="apart" align="center">
            <Text size="sm">
              Showing {total === 0 ? 0 : (page - 1) * perPage + 1} -{" "}
              {Math.min(page * perPage, total)} of {total} entries
            </Text>

            <Pagination
              page={page}
              onChange={(p) => setPage(p)}
              total={totalPages}
            />
          </Group>
        </Stack>
      </Paper>

      <ProgramUpdateModal
        opened={opened}
        onClose={() => {
          setOpened(false);
          setSelectedProgramId(null);
        }}
        programId={selectedProgramId ?? undefined}
      />
    </div>
  );
}
